import { useState } from 'react'
import { ChevronRight, ChevronDown, CheckCircle2, XCircle, Bot } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Markdown } from '../../Markdown'
import { MetricCard } from '../primitives'

interface AgentRendererProps {
  data: unknown
}

interface ToolCallInfo {
  tool: string
  args?: {
    tool_name?: string
    params?: Record<string, unknown>
    category?: string
    [key: string]: unknown
  }
  success?: boolean
}

interface AgentResponse {
  response?: string
  tool_calls?: ToolCallInfo[]
  iterations?: number
  model?: string
  agent?: string
  agent_name?: string // Custom agents use agent_name
  conversation_id?: string
  _hint?: string
}

function formatToolDisplay(call: ToolCallInfo): { name: string; detail: string | null } {
  const baseTool = call.tool

  // For execute_lca_tool, show the actual LCA tool being called
  if (baseTool === 'execute_lca_tool' && call.args?.tool_name) {
    const lcaTool = call.args.tool_name
    const params = call.args.params || {}

    // Build detail string from params
    const details: string[] = []
    if (params.q) details.push(`"${params.q}"`)
    if (params.query) details.push(`"${params.query}"`)
    if (params.database) {
      const db = String(params.database).replace('ecoinvent-', 'ei').replace('-cutoff', '')
      details.push(db)
    }
    if (params.activity_code) details.push(`code: ${params.activity_code}`)

    return {
      name: lcaTool.replace(/_/g, ' '),
      detail: details.length > 0 ? details.join(' · ') : null,
    }
  }

  // For discover tools, show the category
  if (baseTool === 'discover_available_lca_tools') {
    const category = call.args?.category
    return {
      name: 'discover tools',
      detail: category ? `category: ${category}` : null,
    }
  }

  return {
    name: baseTool.replace(/_/g, ' '),
    detail: null,
  }
}

function ToolCallRow({ call, index }: { call: ToolCallInfo; index: number }) {
  const { name, detail } = formatToolDisplay(call)
  const isSuccess = call.success !== false

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 border-b border-border/30 last:border-0',
        'hover:bg-muted/20 transition-colors',
      )}
    >
      <span className="text-xs text-muted-foreground font-mono w-4">{index + 1}</span>

      {isSuccess ? (
        <CheckCircle2 className="w-3.5 h-3.5 text-success flex-shrink-0" />
      ) : (
        <XCircle className="w-3.5 h-3.5 text-destructive flex-shrink-0" />
      )}

      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium">{name}</span>
        {detail && <span className="text-xs text-muted-foreground ml-2 truncate">{detail}</span>}
      </div>
    </div>
  )
}

export function AgentRenderer({ data }: AgentRendererProps) {
  const [showToolCalls, setShowToolCalls] = useState(false)

  // Type guard
  if (!data || typeof data !== 'object') {
    return <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
  }

  const agentData = data as AgentResponse
  const toolCalls = agentData.tool_calls || []
  const hasToolCalls = toolCalls.length > 0

  return (
    <div className="space-y-3">
      {/* Tool Calls Section */}
      {hasToolCalls && (
        <div className="border rounded-lg overflow-hidden">
          <div
            className="flex items-center gap-2 px-3 py-2 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setShowToolCalls(!showToolCalls)}
          >
            {showToolCalls ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
            <Bot className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Agent Actions</span>
            <span className="text-xs text-muted-foreground">({toolCalls.length} tool calls)</span>
          </div>

          {showToolCalls && (
            <div className="divide-y divide-border/30">
              {toolCalls.map((call, i) => (
                <ToolCallRow key={i} call={call} index={i} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Response - Rendered as Markdown */}
      {agentData.response && (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <Markdown>{agentData.response}</Markdown>
        </div>
      )}

      {/* Metadata row */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-border/30">
        {(agentData.agent || agentData.agent_name) && (
          <MetricCard
            label="Agent"
            value={agentData.agent || agentData.agent_name || ''}
            size="sm"
          />
        )}
        {agentData.iterations && (
          <MetricCard label="Iterations" value={agentData.iterations} size="sm" />
        )}
        {agentData.model && (
          <MetricCard
            label="Model"
            value={agentData.model.split('/').pop() || agentData.model}
            size="sm"
          />
        )}
      </div>

      {/* Continuation hint */}
      {agentData.conversation_id && (
        <div className="text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded">
          <span className="font-medium">Continue conversation:</span>{' '}
          <code className="text-[10px] bg-muted px-1 py-0.5 rounded">
            conversation_id="{agentData.conversation_id}"
          </code>
        </div>
      )}
    </div>
  )
}
